import React, { memo, useState } from 'react';
import {
  Accordion,
  Button,
  ButtonSize,
  ButtonVariant,
  CmhcIcon,
  CmhcIconSize,
  FlyoutContainer,
  Link,
  ToastNotification,
  ToastNotificationVariant
} from '@cmhc-schl/hearth-ui';
import { Box, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { MasterPoolStatusEnum } from '../../../HbtMasterPoolTable/types';
import styles from './styles.module.scss';
import { handleMasterPoolDownload } from '../../../HbtMasterPoolTable/utils/FileDownLoad';
import { getMasterpoolsDataSet } from '../../../HbtMasterPoolTable/hooks/MasterPoolDataSetService';
import { useAuthenticationContext } from 'Foundation/Authentication';
import moment from 'moment';
import { getAppErrorReport } from '../../../HbtMasterPoolTable/components/rowActions';
import { config } from '../../../../../../../config';

export const showOnDemandDoc = (statusCode: string, requestType: string) => {
  if (Number(requestType) !== 0) return false;
  return (
    statusCode === MasterPoolStatusEnum.Finalized ||
    statusCode === MasterPoolStatusEnum.Lock ||
    statusCode === MasterPoolStatusEnum.Approved ||
    statusCode === MasterPoolStatusEnum.Expired ||
    false
  );
};

const DocumentAndResourcesFlyout = ({
  statusCode,
  insuredLenderCode,
  masterPoolCmhcAccountNumber,
  requestType
}: {
  statusCode: string;
  insuredLenderCode: string;
  masterPoolCmhcAccountNumber?: string;
  requestType?: string;
}) => {
  const [openFlyout, setOpenFlyout] = useState<boolean>(false);
  const { t } = useTranslation('HbtMasterPoolDetails');
  const authenticationContext = useAuthenticationContext();
  const [showToaster, setShowToaster] = useState(false);

  const [toaster, setToasterMsg] = useState({
    icon: 'icon_check_circle',
    message: {
      header: t('PI-Portfolio-Overview-Flyout-Toaster-Header-Success'),
      subHeader: String(' ')
    }
  });

  const handleToastNotification = (icon: string, message: string, desc = '') => {
    setToasterMsg({
      icon,
      message: {
        header: message,
        subHeader: desc
      }
    });

    setShowToaster(true);
  };

  const toggleResourcesFlyout = (newOpen: boolean) => {
    setOpenFlyout(newOpen);
  };

  const getErrorReport = async () => {
    const masterPoolInventoryDetail =
      config.validationMasterPoolAPi.urls.masterPoolInventoryDetail +
      `/${masterPoolCmhcAccountNumber}/cashier/report`;
    try {
      if (!masterPoolCmhcAccountNumber) {
        throw new Error();
      }
      await getAppErrorReport(
        Number(masterPoolCmhcAccountNumber),
        authenticationContext,
        masterPoolInventoryDetail
      );
    } catch (error) {
      handleToastNotification('icon_info_outlined', t('Download-failed'), t('Download-failed-Sub'));
    }
  };

  const handleFileDownload = async () => {
    const lenderCode = insuredLenderCode;
    const formattedDate = moment().format('YYYY_MM_DD');

    const fileName = `${t('Master-Pool-Datasets')}_${lenderCode}_${masterPoolCmhcAccountNumber}_${formattedDate}.xlsx`;

    await handleMasterPoolDownload({
      authenticationContext,
      fileName: fileName,
      masterPoolCmhcAccountNumber: masterPoolCmhcAccountNumber,
      getMasterpoolsDataSet
    });
  };

  return (
    <Box className={styles.resourcesFlyoutContainer}>
      <Stack direction="row" spacing={2}>
        <Button
          type="button"
          name="documents_resources"
          size={ButtonSize.SMALL}
          variant={ButtonVariant.TERTIARY}
          leadingIcon={() => <CmhcIcon size={CmhcIconSize.SMALL} type="icon_support" />}
          text={{
            value: t('Documents-and-Resources')
          }}
          ariaText={{
            value: t('Documents-and-Resources')
          }}
          onClick={() => toggleResourcesFlyout(true)}
        />
      </Stack>
      <ToastNotification
        icon={toaster.icon}
        dismissMode="autoAndActionable"
        variant={ToastNotificationVariant.INFO}
        open={showToaster}
        header={{ value: toaster.message.header }}
        subHeader={{ value: toaster.message.subHeader }}
        onClose={() => setShowToaster(false)}
      />
      <FlyoutContainer
        id={styles.documentsResourcesFlyoutContainer}
        isHeaderRequired={true}
        headerText={t('Documents-and-Resources')}
        open={openFlyout}
        onClose={() => {
          toggleResourcesFlyout(false);
        }}
        content={
          <Box className={styles.documentsResourcesflyoutContainer} role="presentation">
            <Accordion
              accordionData={{
                heading: t('Flyout-Master-Pool-Details-Portfolio-Insurance-Processing'),
                defaultExpanded: true,
                contentSlot: (
                  <Stack className={styles.documentsResourcesflyoutLinksContainer}>
                    <Box className={styles.documentsResourcesWrapper}>
                      <Link
                        target="_blank"
                        leadingIcon={() => (
                          <CmhcIcon size={CmhcIconSize.MEDIUM} type="icon_msexcel" />
                        )}
                        ariaLabel={t(
                          'Flyout-Master-Pool-Details-Portfolio-Insurance-Processing-File'
                        )}
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          handleFileDownload();
                        }}
                        data-testid="portfolio-insurance-processing-file-link"
                      >
                        {t('Flyout-Master-Pool-Details-Portfolio-Insurance-Processing-File')}
                      </Link>
                    </Box>
                  </Stack>
                )
              }}
            ></Accordion>
            {showOnDemandDoc(statusCode, requestType ?? '') && (
              <Accordion
                accordionData={{
                  heading: t('Flyout-Master-Pool-Details-On-Demand'),
                  defaultExpanded: true,
                  contentSlot: (
                    <Stack className={styles.documentsResourcesflyoutLinksContainer}>
                      <Box
                        className={styles.documentsResourcesWrapper}
                        data-testid="on-demand-error-report-wrapper"
                      >
                        <Button
                          type="button"
                          name={`btn`}
                          size={ButtonSize.SMALL}
                          variant={ButtonVariant.TERTIARY}
                          leadingIcon={() => (
                            <CmhcIcon size={CmhcIconSize.MEDIUM} type="icon_msexcel" />
                          )}
                          onClick={getErrorReport}
                          className={styles.flyoutButton}
                          text={{
                            value: t('Flyout-Master-Pool-Details-On-Demand-File')
                          }}
                          ariaText={{
                            value: t('Flyout-Master-Pool-Details-On-Demand-File')
                          }}
                        />
                      </Box>
                    </Stack>
                  )
                }}
              ></Accordion>
            )}
            <Accordion
              accordionData={{
                heading: t('Flyout-Master-Pool-Details-Resources'),
                defaultExpanded: true,
                contentSlot: (
                  <Stack className={styles.documentsResourcesflyoutLinksContainer}>
                    <Box className={styles.documentsResourcesWrapper}>
                      <Link
                        target="_blank"
                        leadingIcon={() => <CmhcIcon size={CmhcIconSize.MEDIUM} type="icon_pdf" />}
                        ariaLabel={t(
                          'PI-Portfolio-Management-Request-Overview-Insurance-Processing-User-Guide'
                        )}
                        href={t(
                          'PI-Portfolio-Management-Request-Overview-Insurance-Processing-User-Guide-Link'
                        )}
                        data-testid="user-guide-link"
                      >
                        {t(
                          'PI-Portfolio-Management-Request-Overview-Insurance-Processing-User-Guide'
                        )}
                      </Link>
                    </Box>
                    <Box className={styles.documentsResourcesWrapper}>
                      <Link
                        target="_blank"
                        leadingIcon={() => (
                          <CmhcIcon size={CmhcIconSize.MEDIUM} type="icon_file_word" />
                        )}
                        ariaLabel={t(
                          'PI-Portfolio-Management-Request-Overview-Insurance-Processing-Procedures'
                        )}
                        href={t(
                          'PI-Portfolio-Management-Request-Overview-Insurance-Processing-Procedures-Link'
                        )}
                        data-testid="insurance-processing-procedures-link"
                      >
                        {t(
                          'PI-Portfolio-Management-Request-Overview-Insurance-Processing-Procedures'
                        )}
                      </Link>
                    </Box>
                    <Box className={styles.documentsResourcesWrapper}>
                      <Link
                        target="_blank"
                        leadingIcon={() => (
                          <CmhcIcon size={CmhcIconSize.MEDIUM} type="icon_msexcel" />
                        )}
                        ariaLabel={t(
                          'PI-Portfolio-Management-Request-Overview-Insurance-Processing-Rules'
                        )}
                        href={t(
                          'PI-Portfolio-Management-Request-Overview-Insurance-Processing-Rules-Link'
                        )}
                        data-testid="insurance-processing-rules-link"
                      >
                        {t('PI-Portfolio-Management-Request-Overview-Insurance-Processing-Rules')}
                      </Link>
                    </Box>
                    <Box className={styles.documentsResourcesWrapper}>
                      <Link
                        target="_blank"
                        leadingIcon={() => (
                          <CmhcIcon size={CmhcIconSize.MEDIUM} type="icon_msexcel" />
                        )}
                        ariaLabel={t(
                          'PI-Portfolio-Management-Request-Overview-Insurance-Processing-Tracker'
                        )}
                        href={t(
                          'PI-Portfolio-Management-Request-Overview-Insurance-Processing-Tracker-Link'
                        )}
                        data-testid="portfolio-insurance-processing-tracker-link"
                      >
                        {t('PI-Portfolio-Management-Request-Overview-Insurance-Processing-Tracker')}
                      </Link>
                    </Box>
                  </Stack>
                )
              }}
            ></Accordion>
          </Box>
        }
      />
    </Box>
  );
};

export default memo(DocumentAndResourcesFlyout);
